import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { Plus, Edit2, Trash2, Save, X, Calendar, MessageSquare, FileText, AlertCircle, Download, Search } from 'lucide-react'
import { RichTextEditor } from './RichTextEditor'
import { FormattedText } from './FormattedText'
import { ScopePhases } from './ScopePhases'

interface ProjectItem {
  id: string
  original_project_scope: string
  proposed_change: string
  proposed_change_date: string
  sk_comment: string | null
  decision: 'Absorb into Project Scope' | 'Additional Scope & Cost' | 'Shelved for Future Project' | null
  comments: string | null
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
}

export const ProjectTracker: React.FC = () => {
  const { user } = useAuth()
  const [items, setItems] = useState<ProjectItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState<ProjectItem | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [activeTab, setActiveTab] = useState<'tracker' | 'scope'>('scope')
  const [searchQuery, setSearchQuery] = useState('')
  const [formData, setFormData] = useState({
    original_project_scope: '',
    proposed_change: '',
    proposed_change_date: '',
    sk_comment: '',
    decision: null as ProjectItem['decision'],
    comments: ''
  })

  const isAdmin = user?.id === '89927d9d-5bb1-41af-8755-01ffd69d960f'

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    const query = searchQuery.toLowerCase()
    return items.filter(item =>
      item.proposed_change.toLowerCase().includes(query)
    )
  }, [items, searchQuery])

  useEffect(() => {
    fetchItems()
  }, [])

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('project_tracker')
        .select('*')
        .order('proposed_change_date', { ascending: false })

      if (error) throw error
      setItems(data || [])
    } catch (error) {
      console.error('Error fetching project items:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      const sanitizedData = {
        ...formData,
        decision: formData.decision || null
      }

      if (editingItem) {
        const { error } = await supabase
          .from('project_tracker')
          .update({
            ...sanitizedData,
            updated_by: user?.id
          })
          .eq('id', editingItem.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('project_tracker')
          .insert({
            ...sanitizedData,
            created_by: user?.id,
            updated_by: user?.id
          })

        if (error) throw error
      }

      await fetchItems()
      resetForm()
    } catch (error) {
      console.error('Error saving item:', error)
      alert('Failed to save item')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return

    try {
      const { error } = await supabase
        .from('project_tracker')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchItems()
    } catch (error) {
      console.error('Error deleting item:', error)
      alert('Failed to delete item')
    }
  }

  const handleEdit = (item: ProjectItem) => {
    setEditingItem(item)
    setFormData({
      original_project_scope: item.original_project_scope,
      proposed_change: item.proposed_change,
      proposed_change_date: item.proposed_change_date,
      sk_comment: item.sk_comment || '',
      decision: item.decision,
      comments: item.comments || ''
    })
    setIsCreating(true)
  }

  const resetForm = () => {
    setFormData({
      original_project_scope: '',
      proposed_change: '',
      proposed_change_date: '',
      sk_comment: '',
      decision: null,
      comments: ''
    })
    setEditingItem(null)
    setIsCreating(false)
  }

  const getDecisionColor = (decision: ProjectItem['decision']) => {
    switch (decision) {
      case 'Absorb into Project Scope':
        return '#10b981'
      case 'Additional Scope & Cost':
        return '#f59e0b'
      case 'Shelved for Future Project':
        return '#6b7280'
      default:
        return '#e5e7eb'
    }
  }

  const downloadCSV = () => {
    // Helper function to escape CSV fields
    const escapeCSV = (field: string | null | undefined): string => {
      if (field === null || field === undefined) return ''
      const stringField = String(field)
      // Remove HTML tags and formatting
      const cleanField = stringField.replace(/<[^>]*>/g, '').replace(/\n/g, ' ').replace(/\r/g, '')
      // Escape double quotes and wrap in quotes if contains comma, quote, or newline
      if (cleanField.includes(',') || cleanField.includes('"') || cleanField.includes('\n')) {
        return `"${cleanField.replace(/"/g, '""')}"`
      }
      return cleanField
    }

    // Define CSV headers
    const headers = [
      'Date',
      'Original Project Scope',
      'Proposed Change',
      'Decision',
      'SK Comment',
      'Comments',
      'Created At',
      'Updated At'
    ]

    // Convert items to CSV rows
    const rows = items.map(item => [
      escapeCSV(new Date(item.proposed_change_date).toLocaleDateString()),
      escapeCSV(item.original_project_scope),
      escapeCSV(item.proposed_change),
      escapeCSV(item.decision || 'No decision'),
      escapeCSV(item.sk_comment),
      escapeCSV(item.comments),
      escapeCSV(new Date(item.created_at).toLocaleString()),
      escapeCSV(new Date(item.updated_at).toLocaleString())
    ])

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `scope-changes-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <div style={{ color: '#6b7280' }}>Loading project tracker...</div>
      </div>
    )
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)',
      padding: '32px 16px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '8px',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Tech Roadmap
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: '16px',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Track project scope changes and decisions
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('scope')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'scope' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'scope' ? '#2563eb' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              fontFamily: 'Lexend Deca, sans-serif',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Scope
          </button>
          <button
            onClick={() => setActiveTab('tracker')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'tracker' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === 'tracker' ? '#2563eb' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              fontFamily: 'Lexend Deca, sans-serif',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s'
            }}
          >
            Scope Changes
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'scope' ? (
          <ScopePhases />
        ) : (
          <>
        {/* Add New Button, Search Box, and Download CSV Button */}
        {!isCreating && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setIsCreating(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}
            >
              <Plus style={{ height: '20px', width: '20px', marginRight: '8px' }} />
              Add New Item
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              padding: '8px 12px',
              flex: '1',
              minWidth: '200px',
              maxWidth: '400px'
            }}>
              <Search style={{ height: '20px', width: '20px', color: '#9ca3af', marginRight: '8px', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search proposed changes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  background: 'transparent'
                }}
              />
            </div>
            <button
              onClick={downloadCSV}
              disabled={items.length === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: items.length === 0 ? '#e5e7eb' : 'linear-gradient(to right, #10b981, #059669)',
                color: items.length === 0 ? '#9ca3af' : 'white',
                borderRadius: '8px',
                border: 'none',
                cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (items.length > 0) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'
                }
              }}
              onMouseLeave={(e) => {
                if (items.length > 0) {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                }
              }}
            >
              <Download style={{ height: '20px', width: '20px', marginRight: '8px' }} />
              Download CSV
            </button>
          </div>
        )}

        {/* Form */}
        {isCreating && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '20px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              {editingItem ? 'Edit Item' : 'New Item'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    Original Project Scope *
                  </label>
                  <RichTextEditor
                    required
                    value={formData.original_project_scope}
                    onChange={(value) => setFormData({ ...formData, original_project_scope: value })}
                    placeholder="Enter the original project scope..."
                    minHeight="100px"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    Proposed Change *
                  </label>
                  <RichTextEditor
                    required
                    value={formData.proposed_change}
                    onChange={(value) => setFormData({ ...formData, proposed_change: value })}
                    placeholder="Enter the proposed change..."
                    minHeight="100px"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      Proposed Change Date *
                    </label>
                    <input
                      type="date"
                      required
                      disabled={!!editingItem}
                      value={formData.proposed_change_date}
                      onChange={(e) => setFormData({ ...formData, proposed_change_date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: editingItem ? '#f3f4f6' : 'white',
                        color: editingItem ? '#6b7280' : '#111827',
                        cursor: editingItem ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      Decision
                    </label>
                    <select
                      disabled={!!editingItem}
                      value={formData.decision || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          decision:
                            e.target.value === ''
                              ? null
                              : (e.target.value as ProjectItem['decision'])
                        })
                      }
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: editingItem ? '#f3f4f6' : 'white',
                        color: editingItem ? '#6b7280' : '#111827',
                        cursor: editingItem ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">Select decision...</option>
                      <option value="Absorb into Project Scope">Absorb into Project Scope</option>
                      <option value="Additional Scope & Cost">Additional Scope & Cost</option>
                      <option value="Shelved for Future Project">Shelved for Future Project</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    SK Comment
                  </label>
                  <textarea
                    value={formData.sk_comment}
                    onChange={(e) => setFormData({ ...formData, sk_comment: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      minHeight: '60px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    Comments
                  </label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      minHeight: '60px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}
                >
                  <Save style={{ height: '16px', width: '16px', marginRight: '6px' }} />
                  Save
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    background: '#6b7280',
                    color: 'white',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}
                >
                  <X style={{ height: '16px', width: '16px', marginRight: '6px' }} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Items List */}
        <div style={{ display: 'grid', gap: '16px' }}>
          {filteredItems.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <AlertCircle style={{ height: '48px', width: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
              <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                {searchQuery.trim()
                  ? 'No items match your search.'
                  : 'No project tracker items yet. Click "Add New Item" to create one.'}
              </p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  borderLeft: `4px solid ${getDecisionColor(item.decision)}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Calendar style={{ height: '16px', width: '16px', color: '#6b7280', marginRight: '6px' }} />
                      <span style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {new Date(item.proposed_change_date).toLocaleDateString()}
                      </span>
                      {item.decision && (
                        <span style={{
                          marginLeft: '12px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: getDecisionColor(item.decision) + '20',
                          color: getDecisionColor(item.decision),
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          {item.decision}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {(isAdmin || user?.id === item.created_by) && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(item)}
                        style={{
                          padding: '6px',
                          background: '#eff6ff',
                          color: '#2563eb',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <Edit2 style={{ height: '16px', width: '16px' }} />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(item.id)}
                          style={{
                            padding: '6px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2 style={{ height: '16px', width: '16px' }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <FileText style={{ height: '16px', width: '16px', color: '#6b7280', marginRight: '6px', marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                          Original Scope
                        </div>
                        <FormattedText 
                          text={item.original_project_scope}
                          style={{ fontSize: '14px', color: '#111827', lineHeight: '1.5' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <AlertCircle style={{ height: '16px', width: '16px', color: '#f59e0b', marginRight: '6px', marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                          Proposed Change
                        </div>
                        <FormattedText 
                          text={item.proposed_change}
                          style={{ fontSize: '14px', color: '#111827', lineHeight: '1.5' }}
                        />
                      </div>
                    </div>
                  </div>

                  {item.sk_comment && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <MessageSquare style={{ height: '16px', width: '16px', color: '#2563eb', marginRight: '6px', marginTop: '2px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                            SK Comment
                          </div>
                          <div style={{ fontSize: '14px', color: '#111827', lineHeight: '1.5' }}>
                            {item.sk_comment}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {item.comments && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <MessageSquare style={{ height: '16px', width: '16px', color: '#6b7280', marginRight: '6px', marginTop: '2px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                            Comments
                          </div>
                          <div style={{ fontSize: '14px', color: '#111827', lineHeight: '1.5' }}>
                            {item.comments}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        </>
        )}
      </div>
    </div>
  )
}
